import {useState, useEffect} from 'react';

import {CardWidget} from 'shells/card/card-widget/card-widget';
import {FormField} from 'shells/form/form-field';
import type {SavedSettingToastState} from 'shells/saved-setting-toast/saved-setting-toast';
import {CharacterLimitedTextfield} from 'shells/textfield/character-limited-textfield';
import {TextFieldDebounced} from 'shells/textfield/textfield-debounced';

import type {
    ChatActionsFragment_quote as QuoteActionsFragment,
    GetWfFormsChat_forms_edges_node as Form,
    ChatSettingsActionsInput,
} from 'nutshell-graphql-types';

import {AUTO_RESOLVE_ID} from '../../../../core/form/fields/consts';
import {useGetForm} from '../../../../forms/form-page/graphql/hooks/use-get-form';

import {
    SAVE_STATE_KEYS,
    SAVE_DELAY,
    QUOTE_WIDGET,
    formsNoOptionsMessage,
    MAX_LABEL_CHARACTER_LIMIT,
} from './consts';
import type {SaveStateKey, WidgetType} from './consts';
import {getStatusBadge, getFormOptionsWithDefault} from './helpers';
import {LeadCreationToggleSection} from './lead-creation-toggle-section';
import {SelectTwoOptionEngagementEntity} from './select-two-line-engagement-entity';
import {useDebouncedSaveField} from './use-debounced-save-field';

interface Props {
    actionSettings: QuoteActionsFragment;
    forms: Form[] | null | undefined;
    isLoadingForms: boolean;
    openWidget: WidgetType | null | undefined;
    setOpenWidget: (value?: WidgetType | null | undefined) => void;
    toastState: SavedSettingToastState;
    savingToastKey: SaveStateKey | null | undefined;
    handleSave: (updates: ChatSettingsActionsInput, saveStateKey: SaveStateKey) => Promise<any>;
    cardRef?: (arg1?: HTMLDivElement | null | undefined) => void;
}

export function QuoteCard(props: Props) {
    const [isCollapsed, setIsCollapsed] = useState<boolean>(true);

    const savedLabel = props.actionSettings.label || 'Quote';

    const [formConfirmationMessage, setFormConfirmationMessage] = useState<string>(
        props.actionSettings.formConfirmationMessage
    );

    const savedPipeline = props.actionSettings.shouldCreateLeadsPipelineId || AUTO_RESOLVE_ID;
    const [isCreateLeadEnabled, setIsCreateLeadEnabled] = useState<boolean>(
        props.actionSettings.shouldCreateLeads
    );
    const [pipeline, setPipeline] = useState<string>(savedPipeline);

    const [formSlug, setFormSlug] = useState<string>(props.actionSettings.formSlug || '');
    const [selectedForm, setSelectedForm] = useState<any>(null);

    const label = useDebouncedSaveField({
        savedValue: savedLabel,
        handleSave: (newLabel) =>
            props.handleSave({quote: {label: newLabel}}, SAVE_STATE_KEYS.QUOTE_LABEL),
        validation: {isRequired: true, maxLength: MAX_LABEL_CHARACTER_LIMIT},
    });

    const {form: defaultForm} = useGetForm({
        formSlug: props.actionSettings.defaultFormSlug,
    });

    useEffect(() => {
        if (formSlug && props.forms) {
            setSelectedForm(props.forms.find((form) => form.publicFormId === formSlug) || null);
        }
        if (formSlug && defaultForm && defaultForm.publicFormId === formSlug) {
            setSelectedForm(defaultForm);
        }
    }, [formSlug, props.forms, defaultForm]);

    const formOptions = getFormOptionsWithDefault(props.forms || [], defaultForm);

    useEffect(() => {
        // If openWidget's value changes, collapse the widget
        if (props.openWidget !== QUOTE_WIDGET) {
            setIsCollapsed(true);
        }
    }, [props.openWidget]);

    useEffect(() => {
        setPipeline(savedPipeline);
    }, [savedPipeline]);

    return (
        <CardWidget
            cardRef={props.cardRef}
            title='Quote'
            hasBorder={true}
            collapsibleProps={{
                isCollapsed,
                onToggle: () => {
                    setIsCollapsed(!isCollapsed);
                    props.setOpenWidget(isCollapsed ? QUOTE_WIDGET : null);
                },
            }}
            type='QUOTE'
            secondaryTitle={getStatusBadge(props.actionSettings.isConfigured)}
            rowsHavePadding={false}
        >
            <div className='my-16 mx-12'>
                <FormField
                    innerId='action-label'
                    label='Action label'
                    saveToastState={
                        props.savingToastKey === SAVE_STATE_KEYS.QUOTE_LABEL
                            ? props.toastState
                            : 'initial'
                    }
                >
                    <CharacterLimitedTextfield
                        placeholder='Quote'
                        maxLength={MAX_LABEL_CHARACTER_LIMIT}
                        value={label.value}
                        onChange={label.onChange}
                        onBlur={label.onBlur}
                        isDebouncedSave={true}
                        debouncedSaveDelay={SAVE_DELAY}
                    />
                </FormField>
                <FormField
                    innerId='quote-requests'
                    label='Form used for quote requests'
                    saveToastState={
                        props.savingToastKey === SAVE_STATE_KEYS.QUOTE_FORM_SLUG
                            ? props.toastState
                            : 'initial'
                    }
                >
                    <SelectTwoOptionEngagementEntity
                        options={formOptions}
                        placeholder='Select a form…'
                        value={formOptions.find((option) => option.value === formSlug)}
                        onChange={(option) => {
                            setFormSlug(option.value);
                            props.handleSave(
                                {quote: {formSlug: option.value}},
                                SAVE_STATE_KEYS.QUOTE_FORM_SLUG
                            );
                        }}
                        isLoading={props.isLoadingForms}
                        shouldShowEditLink={Boolean(selectedForm)}
                        editLabel='Edit form'
                        editLink={selectedForm && selectedForm.htmlUrlPath}
                        autoFocus={!formSlug}
                        openMenuOnFocus={true}
                        isSearchable={true}
                        customNoOptionsMessage={formsNoOptionsMessage}
                    />
                </FormField>
                <FormField
                    innerId='confirmation-message'
                    label='Confirmation message after completing form'
                    saveToastState={
                        props.savingToastKey === SAVE_STATE_KEYS.QUOTE_CONFIRMATION_MSG
                            ? props.toastState
                            : 'initial'
                    }
                >
                    <TextFieldDebounced
                        value={formConfirmationMessage}
                        onChange={(newMessage) => {
                            setFormConfirmationMessage(newMessage);
                            props.handleSave(
                                {
                                    quote: {
                                        formConfirmationMessage: newMessage,
                                    },
                                },
                                SAVE_STATE_KEYS.QUOTE_CONFIRMATION_MSG
                            );
                        }}
                        multiline={true}
                        placeholder='Thank you for contacting us, looking forward to talking more about your needs!'
                        delay={SAVE_DELAY}
                    />
                </FormField>
                <LeadCreationToggleSection
                    labelContent='Create a lead for each quote request'
                    description='A lead will be created in this pipeline after a visitor completes the quote request form'
                    isOn={isCreateLeadEnabled}
                    onToggle={() => {
                        setIsCreateLeadEnabled(!isCreateLeadEnabled);
                        props.handleSave(
                            {
                                quote: {
                                    shouldCreateLeads: !isCreateLeadEnabled,
                                },
                            },
                            SAVE_STATE_KEYS.QUOTE_SHOULD_CREATE_LEADS
                        );
                    }}
                    toastState={props.toastState}
                    savingToastKey={props.savingToastKey}
                    toggleSaveKey={SAVE_STATE_KEYS.QUOTE_SHOULD_CREATE_LEADS}
                    pipelineSaveKey={SAVE_STATE_KEYS.QUOTE_LEADS_PIPELINE_ID}
                    pipeline={pipeline}
                    onSelectPipeline={(newPipeline) => {
                        setPipeline(newPipeline);
                        props.handleSave(
                            {
                                quote: {
                                    shouldCreateLeadsPipelineId:
                                        newPipeline === AUTO_RESOLVE_ID ? null : newPipeline,
                                },
                            },
                            SAVE_STATE_KEYS.QUOTE_LEADS_PIPELINE_ID
                        );
                    }}
                    showPipeline={isCreateLeadEnabled}
                />
            </div>
        </CardWidget>
    );
}
